import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Minus, Plus } from "lucide-react";
import { BALI } from "../../mock";
import { Reveal, SectionHeading, Eyebrow } from "../common";

/* ------------------------------- Gallery ---------------------------------- */
export function BaliGallery() {
  const [index, setIndex] = useState(0);
  const items = BALI.gallery;
  const perView = 3;
  const max = Math.max(0, items.length - perView);

  useEffect(() => {
    const t = setInterval(() => setIndex((i) => (i >= max ? 0 : i + 1)), 5000);
    return () => clearInterval(t);
  }, [max]);

  return (
    <section className="bg-white py-20 lg:py-24">
      <div className="mx-auto max-w-[1400px] px-5 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-2xl">
            <Eyebrow>Volunteer in Bali</Eyebrow>
            <h2 className="mt-5 font-heading text-[32px] font-extrabold leading-tight tracking-[-0.02em] text-ivi-slate sm:text-[42px]">
              What your weeks actually look like
            </h2>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              aria-label="Previous"
              onClick={() => setIndex((i) => Math.max(0, i - 1))}
              className="grid h-12 w-12 place-items-center rounded-full border border-ivi-mintDeep bg-white text-ivi-slate transition-colors duration-200 hover:bg-ivi-mint"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              aria-label="Next"
              onClick={() => setIndex((i) => Math.min(max, i + 1))}
              className="grid h-12 w-12 place-items-center rounded-full bg-ivi-green text-white transition-colors duration-200 hover:bg-ivi-greenDark"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="mt-10 overflow-hidden">
          <div
            className="flex gap-4 transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]"
            style={{ transform: `translateX(calc(-${index} * (100% / ${perView} + 1rem)))` }}
          >
            {items.map((g) => (
              <figure
                key={g.src + g.caption}
                className="group relative w-[85%] shrink-0 overflow-hidden rounded-3xl sm:w-[48%] lg:w-[calc((100%-2rem)/3)]"
              >
                <img
                  src={g.src}
                  alt={g.caption}
                  loading="lazy"
                  className="h-[300px] w-full object-cover transition-transform duration-[900ms] group-hover:scale-110"
                />
                <span className="absolute inset-0 bg-gradient-to-t from-ivi-slate/80 via-transparent to-transparent" />
                <figcaption className="absolute inset-x-5 bottom-5 font-heading text-[15px] font-bold text-white">
                  {g.caption}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>

        <div className="mt-7 flex justify-center gap-2">
          {Array.from({ length: max + 1 }).map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => setIndex(i)}
              className={`h-2 rounded-full transition-[width,background-color] duration-300 ${
                i === index ? "w-8 bg-ivi-green" : "w-2 bg-ivi-mintDeep"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

/* --------------------------------- FAQs ----------------------------------- */
export function BaliFaqs() {
  const [open, setOpen] = useState(0);
  return (
    <section className="bg-ivi-sand py-24 lg:py-28">
      <div className="mx-auto max-w-[1000px] px-5 lg:px-8">
        <SectionHeading
          eyebrow="Good to know"
          title="Your Bali questions, answered"
          text="Accommodation, food, transport, orientation the practical detail before you commit."
        />
        <div className="mt-12 space-y-3">
          {BALI.faqs.map((f, i) => {
            const isOpen = open === i;
            return (
              <div
                key={f.q}
                className={`overflow-hidden rounded-2xl bg-white ring-1 transition-[box-shadow,ring-color] duration-300 ${
                  isOpen
                    ? "ring-ivi-green shadow-[0_20px_44px_-30px_rgba(24,40,32,0.5)]"
                    : "ring-ivi-mintDeep"
                }`}
              >
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="flex w-full items-center justify-between gap-4 px-7 py-5 text-left"
                >
                  <span className="font-heading text-[17px] font-bold text-ivi-slate">{f.q}</span>
                  <span
                    className={`grid h-9 w-9 shrink-0 place-items-center rounded-full transition-colors duration-200 ${
                      isOpen ? "bg-ivi-green text-white" : "bg-ivi-mint text-ivi-greenDark"
                    }`}
                  >
                    {isOpen ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                  </span>
                </button>
                <div
                  className={`grid transition-[grid-template-rows] duration-400 ease-out ${
                    isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                  }`}
                >
                  <div className="overflow-hidden">
                    <p className="px-7 pb-6 text-[15px] leading-relaxed text-ivi-slateSoft">{f.a}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ------------------------- Weekends & extra tours -------------------------- */
export function BaliTours() {
  const [i, setI] = useState(0);
  const tours = BALI.tours;
  const t = tours[i];

  return (
    <section className="bg-ivi-slate py-24 lg:py-28">
      <div className="mx-auto max-w-[1400px] px-5 lg:px-8">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 font-heading text-[12px] font-bold uppercase tracking-[0.18em] text-ivi-lime ring-1 ring-white/15">
            Weekends &amp; extra tours
          </span>
          <h2 className="mt-5 font-heading text-[32px] font-extrabold leading-tight text-white sm:text-[42px]">
            Your weekends are yours
          </h2>
          <p className="mt-4 text-[17px] leading-relaxed text-white/70">
            Our ground operators help you organise trips at local, non-tourist rates.
          </p>
        </div>

        <div className="mt-12 grid gap-8 lg:grid-cols-[1.1fr_1fr] lg:items-center">
          <div className="relative overflow-hidden rounded-[28px]">
            <img
              key={t.img}
              src={t.img}
              alt={t.name}
              className="h-[380px] w-full object-cover"
              style={{ animation: "heroUp 0.7s cubic-bezier(0.22,1,0.36,1) both" }}
            />
            <span className="absolute inset-0 bg-gradient-to-t from-ivi-slate/70 to-transparent" />
          </div>

          <div>
            <p className="font-heading text-[13px] font-bold uppercase tracking-[0.18em] text-ivi-lime">
              {t.area}
            </p>
            <h3 className="mt-3 font-heading text-[30px] font-extrabold text-white">{t.name}</h3>
            <p className="mt-5 text-[17px] leading-relaxed text-white/75">{t.text}</p>

            <div className="mt-9 flex flex-wrap gap-2">
              {tours.map((x, idx) => (
                <button
                  key={x.name}
                  type="button"
                  onClick={() => setI(idx)}
                  className={`rounded-full px-4 py-2 text-[13px] font-semibold transition-[background-color,color] duration-200 ${
                    idx === i
                      ? "bg-ivi-green text-white"
                      : "bg-white/10 text-white/70 hover:bg-white/20 hover:text-white"
                  }`}
                >
                  {x.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
